import {
  TAuthConverter,
  TConvertor,
  TCreateConverter,
  TFetchConverter,
} from 'data/types/convertor.types';
import {
  TDocPacketUI,
  TDocPacket,
  THPVisitation,
  THPVisitationUI,
  THPContactInfoUI,
  THPContactInfo,
  TNameUpdateUI,
  TNameUpdate,
  TEmailUpdateUI,
  TEmailUpdate,
  TPhoneUpdateUI,
  TPhoneUpdate,
  TFaxUpdateUI,
  TFaxUpdate,
  THPVisitationUpdateUI,
  THPVisitationUpdate,
  EHPDocPacketTypes,
  EHPDocPacketTypesUI,
  TDocPacketGetUI,
  TDocPacketGet,
  THPLetterUpdateUI,
  THPLetterUpdate,
  TDocPacketInfoUI,
  TDocPacketInfoDB,
  TPatientNameUpdate,
  TPatientNameUpdateUI,
  TPatientDOBUpdate,
  TPatientDOBUpdateUI,
} from 'data/types/docPacket.types';
import {
  phoneNumberConverter,
  verificationStatusConverter,
} from './general.converters';
import moment from 'moment';
import { formattedDate } from 'helpers/utils';

export const hpDocTypeConvertor: TConvertor<
  EHPDocPacketTypesUI,
  EHPDocPacketTypes
> = {
  fromDb: data => {
    const obj = {
      [EHPDocPacketTypes.ESA]: EHPDocPacketTypesUI.ESA,
      [EHPDocPacketTypes.SA]: EHPDocPacketTypesUI.SA,
    };
    return obj[data];
  },
  toDb: data => {
    const obj = {
      [EHPDocPacketTypesUI.ESA]: EHPDocPacketTypes.ESA,
      [EHPDocPacketTypesUI.SA]: EHPDocPacketTypes.SA,
    };
    return obj[data];
  },
};

export const hpVisitationConvertor: TCreateConverter<
  THPVisitationUI,
  THPVisitation
> = {
  toDb: data => {
    return {
      websiteAdvertised: data.websiteAdvertised === 'yes',
      websites: data.websites
        ? [data.websites]?.filter(website => !!website)
        : undefined,
    };
  },
};

export const hpContactInfoConvertor: TAuthConverter<
  THPContactInfoUI,
  THPContactInfo,
  THPContactInfoUI,
  THPContactInfo
> = {
  toDb: data => ({
    name: data.name,
    emailAddress: data.emailAddress?.trim() || undefined,
    phoneNumber: phoneNumberConverter.toDb(data.phoneNumber) || undefined,
    faxNumber: data.faxNumber?.trim() || undefined,
    patientName: data.patientName.trim(),
    patientDateOfBirth: data.patientDateOfBirth
      ? formattedDate(data.patientDateOfBirth, 'YYYY-MM-DD')
      : null,
  }),
  fromDb: data => ({
    name: data.name,
    emailAddress: data.emailAddress,
    phoneNumber: data.phoneNumber,
    faxNumber: data.faxNumber,
    patientName: data?.patientName,
    patientDateOfBirth: data?.patientDateOfBirth
      ? moment(data.patientDateOfBirth)
      : null,
  }),
};

export const hpNameUpdateConvertor: TCreateConverter<
  TNameUpdateUI,
  TNameUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpContactName: data.value || null,
  }),
};
export const patientNameUpdateConvertor: TCreateConverter<
  TPatientNameUpdateUI,
  TPatientNameUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    patientName: data.value || null,
  }),
};

export const patientDOBUpdateConvertor: TCreateConverter<
  TPatientDOBUpdateUI,
  TPatientDOBUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    patientDOB: data.value ? formattedDate(data.value, 'YYYY-MM-DD') : null,
  }),
};

export const hpEmailUpdateConvertor: TCreateConverter<
  TEmailUpdateUI,
  TEmailUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpContactEmail: data.value || null,
  }),
};

export const hpPhoneUpdateConvertor: TCreateConverter<
  TPhoneUpdateUI,
  TPhoneUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpContactPhone: phoneNumberConverter.toDb(data.value),
  }),
};

export const hpFaxUpdateConvertor: TCreateConverter<TFaxUpdateUI, TFaxUpdate> =
  {
    toDb: data => ({
      documentPacketId: data.documentPacketId,
      hpContactFaxNumber: data.value || null,
    }),
  };

export const hpVisitationUpdateConvertor: TCreateConverter<
  THPVisitationUpdateUI,
  THPVisitationUpdate
> = {
  toDb: data => {
    const hpVisitation = data.websiteAdvertised
      ? {
          websiteAdvertised: data.websiteAdvertised === 'yes',
          websites: data.websites ? [data.websites] : undefined,
        }
      : null;
    const receivedAfterCompletingQuestionnaireOrInterview =
      data.receivedAfterCompletingQuestionnaireOrInterview
        ? data.receivedAfterCompletingQuestionnaireOrInterview === 'yes'
        : null;
    return {
      documentPacketId: data.documentPacketId,
      hpVisitation,
      receivedAfterCompletingQuestionnaireOrInterview,
    };
  },
};

export const hpLetterUpdateConvertor: TCreateConverter<
  THPLetterUpdateUI,
  THPLetterUpdate
> = {
  toDb: data => ({
    documentPacketId: data.hpDocumentPacketId,
    newHpLetters: data.newHpLetters || [],
    removedHpLetterIds: data.removedDocumentsIds || [],
  }),
};

export const getHPDocPacketsConvertor: TFetchConverter<
  TDocPacketGetUI,
  TDocPacketGet
> = {
  fromDb: data => {
    return {
      hpDocumentPacketId: data.hpDocumentPacketId,
      type: hpDocTypeConvertor.fromDb(data.type),
      hpContactInfo: hpContactInfoConvertor.fromDb(data.hpContactInfo),
      websites: data.hpVisitation?.websites?.map(item => item && item.value),
      hpVisitation: data.hpVisitation?.websiteAdvertised,
      hpDocForms: data.hpDocForms,
      hpDocLetters: data.hpDocLetters,
      verificationStatus: data.verificationStatus,
      canEdit: data.modifiable,
      status: verificationStatusConverter.fromDb(data.verificationStatus),
      receivedAfterCompletingQuestionnaireOrInterview:
        data.receivedAfterCompletingQuestionnaireOrInterview,
      patientName: data?.patientName || '',
      patientDateOfBirth: data.patientDateOfBirth
        ? moment(data.patientDateOfBirth)
        : null,
      roiSignature: data.roiSignature,
    };
  },
};

export const createHPDocConvertor: TCreateConverter<TDocPacketUI, TDocPacket> =
  {
    toDb: data => ({
      hpContactInfo: hpContactInfoConvertor.toDb({
        name: data.name,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber,
        faxNumber: data.faxNumber,
        patientName: data.patientName,
        patientDateOfBirth: data.patientDateOfBirth,
      }),
      // hpDocType: data.hpDocType
      //   ? hpDocTypeConvertor.toDb(data.hpDocType)
      //   : undefined,
      hpVisitation: data.websiteAdvertised
        ? hpVisitationConvertor.toDb({
            websiteAdvertised: data.websiteAdvertised,
            websites: data.websiteAdvertised,
          })
        : undefined,
      hpDocPacketLetter: data.hpDocPacketLetter ?? [],
      receivedAfterCompletingQuestionnaireOrInterview: null,
    }),
  };

export const createHPDocInfoConvertor: TCreateConverter<
  TDocPacketInfoUI,
  TDocPacketInfoDB
> = {
  toDb: data => {
    const hpVisitation = data.websiteAdvertised
      ? {
          websiteAdvertised: data.websiteAdvertised === 'yes',
          websites: data.websites ? [data.websites] : undefined,
        }
      : null;
    const receivedAfterCompletingQuestionnaireOrInterview =
      data.receivedAfterCompletingQuestionnaireOrInterview
        ? data.receivedAfterCompletingQuestionnaireOrInterview === 'yes'
        : null;

    return {
      hpDocumentPacketId: data.hpDocumentPacketId,
      receivedAfterCompletingQuestionnaireOrInterview,
      hpVisitation,
      roiSignature: data.roiSignature,
    };
  },
};
